import { useEffect, useRef, useState } from 'react';
import { setupDataFetcher } from './setupDataFetcher';

export interface OrderBookBuysSells {
  buys: { price: string; quantity: string }[];
  sells: { price: string; quantity: string }[];
}

export interface OrderBook extends OrderBookBuysSells {
  spread?: number;
  maxBuyAmount?: string;
  maxSellAmount?: string;
}

export function useOrderBook({ marketId }: { marketId: string }, refreshInterval = 5000) {
  const [data, setData] = useState<OrderBook>();
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);
  const disposer = useRef(() => {});

  const setupMarketsFetcher = () => {
    setData(undefined);
    setLoading(true);
    setError(false);

    if (!marketId) return;

    const onResult = message => {
      setData(message);
      setLoading(false);
      setError(false);
    };
    const onError = message => {
      console.error(message);
      setError(true);
      setLoading(false);
      disposer.current?.();
    };

    const generateFetchKey = ({
      marketId = undefined,
      rawData = undefined
    }: {
      marketId?: string;
      rawData?: unknown;
    } = {}) => {
      if (marketId && rawData) {
        return `orderBook-rawData-${marketId}`;
      }
      if (marketId && !rawData) {
        return `orderBook-${marketId}`;
      }
      return 'orderBook';
    };

    disposer.current = setupDataFetcher({
      onResult,
      onError,
      generateFetchKey,
      refreshInterval,
      maxDataAge: 60000, // 1 mins,
      propParams: {
        marketId
      }
    }).disposer;
  };

  useEffect(() => {
    setupMarketsFetcher();
    return () => disposer.current?.();
  }, [marketId]);

  const refetch = () => {
    disposer.current?.();
    setupMarketsFetcher();
  };

  return {
    data,
    loading,
    error,
    refetch
  };
}
